import { useState } from "react";
import { Video, Copy, Wand2, Sparkles, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAdminAuth } from "@/lib/AdminAuthContext";
import { adminFetchJson } from "@/lib/adminFetch";
import AdminLayout from "./AdminLayout";

const SUGGESTIONS = [
  "Glutatión y energía mujer 40+",
  "Mito del cansancio después de los 35",
  "Qué nadie te dice sobre la menopausia",
  "Por qué dejé el café por XTRIVA",
  "Mi mañana en 5 minutos",
];

function ReelCard({ reel, index }) {
  const copy = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copiado`);
  };

  if (!reel.ok) {
    return (
      <div className="rounded-2xl border border-[#bc6c25]/30 bg-[#bc6c25]/5 p-5 flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 text-[#bc6c25] mt-0.5" />
        <p className="text-sm text-[#1a2421]">Falló este reel ({reel.error || "sin detalle"}). Reintenta.</p>
      </div>
    );
  }

  const fullScript = [
    `🎬 ${reel.title || "Reel"}`,
    `Duración: ~${reel.estimated_duration_sec || 45}s`,
    "",
    `🔥 HOOK (0-3s):`,
    reel.hook,
    "",
    "🎞 BEATS:",
    ...(reel.beats || []).map((b) => `  [${b.time}] VOZ: ${b.voice}\n         B-ROLL: ${b.broll}`),
    "",
    `📣 CTA: ${reel.cta}`,
    "",
    "—— Caption ——",
    reel.caption_es,
    "",
    (reel.hashtags || []).join(" "),
  ].join("\n");

  return (
    <div data-testid={`reel-card-${index}`} className="rounded-2xl border border-[#2c4a3b]/10 bg-white overflow-hidden shadow-[0_2px_12px_-6px_rgba(26,36,33,0.15)]">
      <div className="p-5 border-b border-[#2c4a3b]/10 bg-gradient-to-r from-[#1a2421] to-[#2c4a3b] text-[#f9f6f0]">
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="text-[10px] uppercase tracking-[0.22em] text-[#bc6c25] font-semibold">{reel.angle_label}</p>
            <h3 className="font-serif text-xl mt-1 leading-snug">{reel.title}</h3>
          </div>
          <div className="text-right">
            <p className="text-[10px] uppercase tracking-[0.2em] text-white/60">Duración</p>
            <p className="text-2xl font-serif">{reel.estimated_duration_sec || 45}s</p>
          </div>
        </div>
      </div>

      <div className="p-5">
        {/* HOOK */}
        <div className="p-4 rounded-xl bg-[#bc6c25]/10 border border-[#bc6c25]/30 mb-4">
          <div className="flex items-center justify-between mb-1">
            <p className="text-[10px] uppercase tracking-[0.22em] text-[#bc6c25] font-bold">Hook · 0-3s</p>
            <button
              onClick={() => copy(reel.hook, "Hook")}
              className="text-[10px] text-[#bc6c25] hover:underline"
              data-testid={`copy-hook-${index}`}
            >
              copiar
            </button>
          </div>
          <p className="text-[15px] leading-snug text-[#1a2421] font-medium">"{reel.hook}"</p>
        </div>

        {/* BEATS */}
        <div className="space-y-3 mb-4">
          {(reel.beats || []).map((b, i) => (
            <div key={i} className="grid grid-cols-[60px_1fr] gap-3">
              <span className="text-[10px] uppercase tracking-[0.18em] text-[#5c6b61] font-bold pt-0.5">{b.time}</span>
              <div>
                <p className="text-sm text-[#1a2421] leading-relaxed"><strong className="text-[#2c4a3b]">VOZ:</strong> {b.voice}</p>
                <p className="text-xs text-[#5c6b61] mt-1"><strong>B-ROLL:</strong> {b.broll}</p>
              </div>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="p-3 rounded-xl bg-[#2c4a3b]/5 border border-[#2c4a3b]/15 mb-4">
          <p className="text-[10px] uppercase tracking-[0.22em] text-[#2c4a3b] font-bold mb-1">CTA</p>
          <p className="text-sm text-[#1a2421]">{reel.cta}</p>
        </div>

        {/* Caption */}
        <details className="mb-3">
          <summary className="text-[10px] uppercase tracking-[0.22em] text-[#5c6b61] font-bold cursor-pointer">
            Caption sugerido · {(reel.caption_es || "").length} caracteres
          </summary>
          <pre className="mt-2 text-[13px] leading-relaxed text-[#1a2421] whitespace-pre-wrap font-sans bg-[#f0ebe1] p-3 rounded-lg max-h-44 overflow-y-auto">{reel.caption_es}</pre>
          <div className="mt-2 text-[11px] text-[#5c6b61] bg-[#f0ebe1] p-2 rounded-lg max-h-20 overflow-y-auto">
            {(reel.hashtags || []).join(" ")}
          </div>
        </details>

        <div className="flex gap-2">
          <Button
            onClick={() => copy(fullScript, "Guión completo")}
            size="sm"
            className="flex-1 bg-[#2c4a3b] hover:bg-[#1f3529]"
            data-testid={`copy-reel-${index}`}
          >
            <Copy className="w-3.5 h-3.5 mr-1.5" /> Copiar guión completo
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function ReelsStudio() {
  useAdminAuth();
  const [theme, setTheme] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const generate = async (selected) => {
    const t = (selected || theme).trim();
    if (t.length < 2) { toast.error("Escribe un tema"); return; }
    setLoading(true);
    setResult(null);
    try {
      const data = await adminFetchJson("/api/admin/content/reels", {
        method: "POST",
        body: JSON.stringify({ theme: t, count: 3 }),
      });
      setResult(data);
      const okCount = (data.reels || []).filter((r) => r.ok).length;
      toast.success(`${okCount} de ${data.count} reels generados`);
    } catch (e) {
      toast.error(`No se pudo: ${e.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AdminLayout>
      <div className="mb-8">
        <p className="text-[10px] uppercase tracking-[0.28em] text-[#bc6c25] font-semibold">Reels Studio</p>
        <h1 className="font-serif text-3xl md:text-4xl text-[#1a2421] mt-1">Guiones virales</h1>
        <p className="text-sm text-[#5c6b61] mt-2 max-w-2xl">
          Genera 3 guiones de Reel de 30-60s con la estructura viral: hook que detiene el scroll, 3-4 beats con
          indicaciones de voz + B-roll, CTA suave y caption listo para publicar.
        </p>
      </div>

      <div className="rounded-2xl border border-[#2c4a3b]/10 bg-white p-5 md:p-6">
        <div className="flex flex-col md:flex-row gap-3">
          <Input
            data-testid="reel-theme-input"
            placeholder="Ej: glutatión y energía mujer 40+, mito del cansancio..."
            value={theme}
            onChange={(e) => setTheme(e.target.value)}
            disabled={loading}
            className="flex-1"
          />
          <Button
            onClick={() => generate()}
            disabled={loading}
            data-testid="reel-generate-btn"
            className="bg-[#bc6c25] hover:bg-[#9c581e] text-white px-6"
          >
            {loading ? (
              <><Sparkles className="w-4 h-4 mr-2 animate-pulse" /> Generando 3 guiones…</>
            ) : (
              <><Wand2 className="w-4 h-4 mr-2" /> Generar 3 Reels</>
            )}
          </Button>
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          <span className="text-[10px] uppercase tracking-[0.2em] text-[#5c6b61] mr-1 self-center">Sugerencias:</span>
          {SUGGESTIONS.map((s) => (
            <button
              key={s}
              onClick={() => { setTheme(s); generate(s); }}
              disabled={loading}
              className="text-xs px-3 py-1.5 rounded-full bg-[#f0ebe1] hover:bg-[#bc6c25]/10 text-[#1a2421] disabled:opacity-50"
            >
              {s}
            </button>
          ))}
        </div>
        {loading && <p className="mt-4 text-sm text-[#5c6b61]">15-30 segundos. GPT-5.2 escribiendo guiones en paralelo…</p>}
      </div>

      {result && (
        <div className="mt-10">
          <div className="flex items-center gap-2 mb-5">
            <Video className="w-5 h-5 text-[#bc6c25]" />
            <p className="text-[10px] uppercase tracking-[0.22em] text-[#bc6c25] font-semibold">Tema: {result.theme}</p>
          </div>
          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-5">
            {(result.reels || []).map((r, i) => <ReelCard key={i} reel={r} index={i} />)}
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
